import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "axios";
import { Eye, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  previewClientInvoiceEmail,
  type InvoiceEmailTemplate,
} from "@/services/clientService";

export function InvoiceEmailPreviewButton(props: {
  getPayload: () => {
    invoiceEmailTemplate: InvoiceEmailTemplate;
    invoiceEmailCustomBody?: string;
    invoiceEmailIncludeCompanyLogo?: boolean;
    invoiceEmailIncludeInvoiceDetails?: boolean;
  };
  disabled?: boolean;
}): ReactElement {
  const { getPayload, disabled } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = async (): Promise<void> => {
    setLoading(true);
    try {
      const payload = getPayload();
      const { html: nextHtml } = await previewClientInvoiceEmail({
        invoiceEmailTemplate: payload.invoiceEmailTemplate,
        invoiceEmailCustomBody:
          payload.invoiceEmailTemplate === "custom"
            ? payload.invoiceEmailCustomBody
            : undefined,
        invoiceEmailIncludeCompanyLogo:
          payload.invoiceEmailTemplate === "custom"
            ? payload.invoiceEmailIncludeCompanyLogo
            : undefined,
        invoiceEmailIncludeInvoiceDetails:
          payload.invoiceEmailTemplate === "custom"
            ? payload.invoiceEmailIncludeInvoiceDetails
            : undefined,
      });
      setHtml(nextHtml);
      setOpen(true);
    } catch (err: unknown) {
      let message = t("clients.form.previewInvoiceEmailError");
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { message?: string | string[] }
          | undefined;
        const raw = data?.message;
        if (typeof raw === "string" && raw.trim()) message = raw;
        else if (Array.isArray(raw) && raw.length > 0) message = raw.join(" ");
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="transition-colors duration-150"
        disabled={disabled || loading}
        onClick={() => void handleOpen()}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Eye className="mr-2 h-4 w-4" aria-hidden />
        )}
        {t("clients.form.previewInvoiceEmail")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden p-0 sm:max-w-3xl">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle className="text-foreground">
              {t("clients.form.previewInvoiceEmailTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="relative min-h-[50vh] bg-muted/30">
            {html != null ? (
              <iframe
                title={t("clients.form.previewInvoiceEmailTitle")}
                sandbox="allow-same-origin"
                className="h-[min(70vh,560px)] w-full border-0 bg-white"
                srcDoc={html}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
